import api from 'api'

import { CACHE_TAG_INVENTORY_CONTAINER_LIST, CACHE_TAG_CONFIGURED_SERVICE_CONTAINER_LIST } from '../cacheTagTypes'

import type { ContainerApi, ContainerListResponseApi } from 'types/api/container/container'
import type {
  ContainerBody,
  ContainerListBody,
  ContainerMetaPatchBody,
  ContainerMetaPatchListBody,
  GetContainersParams,
} from 'types/api/container/containerRequests'

const extendedApi = api.injectEndpoints({
  endpoints: builder => ({
    getContainers: builder.query<ContainerListResponseApi, GetContainersParams | undefined>({
      query: params => ({
        url: '/api/v1/containers',
        method: 'GET',
        params,
      }),
      providesTags: [CACHE_TAG_INVENTORY_CONTAINER_LIST],
    }),
    getContainerDetails: builder.query<ContainerApi, { id: string }>({
      query: ({ id }) => ({
        url: `/api/v1/containers/${id}`,
        method: 'GET',
      }),
      providesTags: [CACHE_TAG_INVENTORY_CONTAINER_LIST],
    }),
    // The four write endpoints stay tag-free: callers refetch or invalidate by hand today, and a tag here would change when lists reload.
    createContainer: builder.mutation<ContainerApi, ContainerBody>({
      query: body => ({
        url: '/api/v1/containers',
        method: 'POST',
        body,
      }),
      invalidatesTags: [],
    }),
    bulkCreateContainer: builder.mutation<ContainerListResponseApi, ContainerListBody>({
      query: body => ({
        url: '/api/v1/containers/bulk',
        method: 'POST',
        body,
      }),
      invalidatesTags: [],
    }),
    updateContainer: builder.mutation<ContainerApi, ContainerBody & { id: string }>({
      query: ({ id, ...rest }) => ({
        url: `/api/v1/containers/${id}`,
        method: 'PUT',
        body: { ...rest },
      }),
      invalidatesTags: [],
    }),
    bulkUpdateContainer: builder.mutation<ContainerListResponseApi, ContainerListBody>({
      query: body => ({
        url: `/api/v1/containers/bulk`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [],
    }),
    patchContainer: builder.mutation<ContainerApi, ContainerMetaPatchBody>({
      query: ({ id, ...rest }) => ({
        url: `/api/v1/containers/${id}`,
        method: 'PATCH',
        body: { ...rest },
      }),
      invalidatesTags: [CACHE_TAG_INVENTORY_CONTAINER_LIST],
    }),
    bulkPatchContainer: builder.mutation<ContainerListResponseApi, ContainerMetaPatchListBody>({
      query: body => ({
        url: `/api/v1/containers/bulk`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, arg) =>
        arg?.skipInvalidate ? [] : [CACHE_TAG_INVENTORY_CONTAINER_LIST, CACHE_TAG_CONFIGURED_SERVICE_CONTAINER_LIST],
    }),
    deleteContainer: builder.mutation<unknown, { id: string }>({
      query: ({ id }) => ({
        url: `/api/v1/containers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [CACHE_TAG_INVENTORY_CONTAINER_LIST],
    }),
  }),
})

export const {
  useGetContainersQuery,
  useLazyGetContainersQuery,
  useGetContainerDetailsQuery,
  useLazyGetContainerDetailsQuery,
  useCreateContainerMutation,
  useBulkCreateContainerMutation,
  useUpdateContainerMutation,
  useBulkUpdateContainerMutation,
  usePatchContainerMutation,
  useBulkPatchContainerMutation,
  useDeleteContainerMutation,
} = extendedApi
